import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, Sparkles, Trash2, Plus, Link as LinkIcon, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { ZONE_META } from "./ZoneVisualizer";

const emptyZone = (type = "long_entry") => ({
  type, price_min: "", price_max: "", label: ""
});

const emptyDraft = {
  asset_name: "",
  symbol: "",
  yahoo_symbol: "",
  category: "Aktien",
  direction: "long",
  zones: [],
  notes: "",
  source_url: "",
  source_type: "manual",
  is_active: true,
};

function setupToDraft(setup) {
  if (!setup) return emptyDraft;
  return {
    asset_name: setup.asset_name || "",
    symbol: setup.symbol || "",
    yahoo_symbol: setup.yahoo_symbol || "",
    category: setup.category || "Aktien",
    direction: setup.direction || "long",
    zones: (setup.zones || []).map(z => ({
      type: z.type,
      price_min: z.price_min,
      price_max: z.price_max,
      label: z.label || "",
    })),
    notes: setup.notes || "",
    source_url: setup.source_url || "",
    source_type: setup.source_type || "manual",
    is_active: setup.is_active !== false,
  };
}

export default function AddSetupSheet({ open, onOpenChange, onCreated, editSetup = null, onUpdated }) {
  const isEdit = Boolean(editSetup);
  const [tab, setTab] = useState("url");
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);

  useEffect(() => {
    if (open) {
      setDraft(setupToDraft(editSetup));
      setTab(isEdit ? "manual" : "url");
      setUrl(""); setText("");
    }
  }, [open, editSetup, isEdit]);

  const reset = () => { setDraft(emptyDraft); setUrl(""); setText(""); };

  const runAnalyzeUrl = async () => {
    if (!url.trim()) { toast.error("Bitte URL eingeben."); return; }
    setAnalyzing(true);
    try {
      const data = await api.analyzeUrl(url.trim());
      setDraft({ ...emptyDraft, ...data });
      if (data.warning) toast.warning(data.warning);
      else toast.success("Analyse abgeschlossen. Bitte prüfen und speichern.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analyse fehlgeschlagen.");
    } finally {
      setAnalyzing(false);
    }
  };

  const runAnalyzeText = async () => {
    if (text.trim().length < 30) { toast.error("Bitte mindestens 30 Zeichen Text einfügen."); return; }
    setAnalyzing(true);
    try {
      const data = await api.analyzeText(text.trim());
      setDraft({ ...emptyDraft, ...data });
      toast.success("Analyse abgeschlossen. Bitte prüfen und speichern.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analyse fehlgeschlagen.");
    } finally {
      setAnalyzing(false);
    }
  };

  const updateZone = (idx, field, val) => {
    const zones = [...draft.zones];
    zones[idx] = { ...zones[idx], [field]: val };
    setDraft({ ...draft, zones });
  };

  const addZone = () => setDraft({ ...draft, zones: [...draft.zones, emptyZone(draft.direction === "long" ? "long_entry" : "short_entry")] });
  const removeZone = (idx) => setDraft({ ...draft, zones: draft.zones.filter((_, i) => i !== idx) });

  const save = async () => {
    if (!draft.asset_name || !draft.symbol) {
      toast.error("Asset-Name und TradingView-Symbol sind erforderlich.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...draft,
        yahoo_symbol: draft.yahoo_symbol || null,
        zones: draft.zones
          .filter(z => z.price_min !== "" && z.price_min !== null)
          .map(z => ({
            type: z.type,
            price_min: parseFloat(z.price_min),
            price_max: parseFloat(z.price_max || z.price_min),
            label: z.label || null,
          })),
      };
      if (isEdit) {
        const updated = await api.updateSetup(editSetup.id, payload);
        toast.success("Setup aktualisiert.");
        onOpenChange(false);
        onUpdated?.(updated);
      } else {
        const created = await api.createSetup(payload);
        toast.success("Setup gespeichert.");
        reset();
        onOpenChange(false);
        onCreated?.(created);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl bg-background border-l border-border text-foreground overflow-y-auto"
        data-testid="add-setup-sheet"
      >
        <SheetHeader>
          <SheetTitle className="font-display text-2xl tracking-tight">
            {isEdit ? "Setup bearbeiten" : "Neues Setup"}
          </SheetTitle>
          <SheetDescription className="text-muted-foreground">
            {isEdit ? "Ändere Felder und Zonen — neue Zonen-Trigger werden zurückgesetzt." :
              "Friedrich-Report URL einfügen, Text einfügen oder manuell anlegen."}
          </SheetDescription>
        </SheetHeader>

        {!isEdit && (
          <Tabs value={tab} onValueChange={setTab} className="mt-6">
            <TabsList className="bg-muted border border-border rounded-sm">
              <TabsTrigger value="url" data-testid="tab-url" className="rounded-sm data-[state=active]:bg-background">
                <LinkIcon size={14} className="mr-2" /> Friedrich URL
              </TabsTrigger>
              <TabsTrigger value="text" data-testid="tab-text" className="rounded-sm data-[state=active]:bg-background">
                <FileText size={14} className="mr-2" /> E-Mail / Text
              </TabsTrigger>
              <TabsTrigger value="manual" data-testid="tab-manual" className="rounded-sm data-[state=active]:bg-background">
                <Plus size={14} className="mr-2" /> Manuell
              </TabsTrigger>
            </TabsList>

            <TabsContent value="url" className="mt-4 space-y-3">
              <Label className="text-muted-foreground text-xs uppercase tracking-wider">Report URL</Label>
              <Input
                data-testid="input-friedrich-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://friedrich.report/post/..."
                className="bg-muted border-border font-mono-num text-sm"
              />
              <Button
                data-testid="btn-analyze-url"
                onClick={runAnalyzeUrl}
                disabled={analyzing}
                className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm font-display font-bold uppercase tracking-wider"
              >
                {analyzing ? <><Loader2 size={14} className="mr-2 animate-spin" /> Analysiere...</> : <><Sparkles size={14} className="mr-2" /> Analysieren</>}
              </Button>
              <p className="text-[10px] text-muted-foreground">
                Hinweis: Friedrich-Report ist paywalled. Bei limitiertem Zugriff bitte Tab "E-Mail / Text" nutzen.
              </p>
            </TabsContent>

            <TabsContent value="text" className="mt-4 space-y-3">
              <Label className="text-muted-foreground text-xs uppercase tracking-wider">E-Mail-Inhalt einfügen</Label>
              <Textarea
                data-testid="input-friedrich-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Hier den E-Mail-Text oder Report-Inhalt einfügen..."
                rows={10}
                className="bg-muted border-border text-sm"
              />
              <Button
                data-testid="btn-analyze-text"
                onClick={runAnalyzeText}
                disabled={analyzing}
                className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm font-display font-bold uppercase tracking-wider"
              >
                {analyzing ? <><Loader2 size={14} className="mr-2 animate-spin" /> Analysiere...</> : <><Sparkles size={14} className="mr-2" /> Analysieren</>}
              </Button>
            </TabsContent>

            <TabsContent value="manual" className="mt-4 space-y-3">
              <p className="text-xs text-muted-foreground">Felder unten direkt ausfüllen.</p>
            </TabsContent>
          </Tabs>
        )}

        {/* Editable draft form */}
        <div className="mt-6 space-y-4 border-t border-border pt-6">
          <h3 className="font-display font-bold text-lg uppercase tracking-wider text-foreground">Setup Details</h3>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-muted-foreground text-xs uppercase tracking-wider">Asset Name</Label>
              <Input
                data-testid="input-asset-name"
                value={draft.asset_name}
                onChange={(e) => setDraft({ ...draft, asset_name: e.target.value })}
                placeholder="Gold"
                className="bg-muted border-border mt-1"
              />
            </div>
            <div>
              <Label className="text-muted-foreground text-xs uppercase tracking-wider">TradingView Symbol</Label>
              <Input
                data-testid="input-symbol"
                value={draft.symbol}
                onChange={(e) => setDraft({ ...draft, symbol: e.target.value })}
                placeholder="OANDA:XAUUSD"
                className="bg-muted border-border mt-1 font-mono-num"
              />
            </div>
            <div>
              <Label className="text-muted-foreground text-xs uppercase tracking-wider">Yahoo Symbol (für Live-Preise)</Label>
              <Input
                data-testid="input-yahoo-symbol"
                value={draft.yahoo_symbol}
                onChange={(e) => setDraft({ ...draft, yahoo_symbol: e.target.value })}
                placeholder="GC=F"
                className="bg-muted border-border mt-1 font-mono-num"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Optional. Lass leer für Auto-Erkennung.</p>
            </div>
            <div>
              <Label className="text-muted-foreground text-xs uppercase tracking-wider">Kategorie</Label>
              <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
                <SelectTrigger data-testid="select-category" className="bg-muted border-border mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background border-border text-foreground">
                  {["Aktien","Gold","Rohstoff","Krypto","Forex","Index"].map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-muted-foreground text-xs uppercase tracking-wider">Richtung</Label>
              <Select value={draft.direction} onValueChange={(v) => setDraft({ ...draft, direction: v })}>
                <SelectTrigger data-testid="select-direction" className="bg-muted border-border mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background border-border text-foreground">
                  <SelectItem value="long">LONG</SelectItem>
                  <SelectItem value="short">SHORT</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Zones editor */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-muted-foreground text-xs uppercase tracking-wider">Zonen</Label>
              <Button
                size="sm"
                data-testid="btn-add-zone"
                onClick={addZone}
                variant="outline"
                className="h-7 border-border bg-transparent hover:bg-muted text-xs"
              >
                <Plus size={12} className="mr-1" /> Zone
              </Button>
            </div>
            {draft.zones.length === 0 && (
              <div className="text-muted-foreground text-xs border border-dashed border-border p-3">
                Noch keine Zonen. Klicke auf "Zone" um eine hinzuzufügen.
              </div>
            )}
            {draft.zones.map((z, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`zone-row-${idx}`}>
                <div className="col-span-3">
                  <Select value={z.type} onValueChange={(v) => updateZone(idx, "type", v)}>
                    <SelectTrigger className="bg-muted border-border h-9 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-background border-border text-foreground">
                      {Object.entries(ZONE_META).map(([k, m]) => (
                        <SelectItem key={k} value={k}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-3">
                  <Input
                    type="number"
                    step="any"
                    placeholder="Min"
                    value={z.price_min}
                    onChange={(e) => updateZone(idx, "price_min", e.target.value)}
                    className="bg-muted border-border h-9 font-mono-num text-xs"
                  />
                </div>
                <div className="col-span-3">
                  <Input
                    type="number"
                    step="any"
                    placeholder="Max"
                    value={z.price_max}
                    onChange={(e) => updateZone(idx, "price_max", e.target.value)}
                    className="bg-muted border-border h-9 font-mono-num text-xs"
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    placeholder="Label"
                    value={z.label || ""}
                    onChange={(e) => updateZone(idx, "label", e.target.value)}
                    className="bg-muted border-border h-9 text-xs"
                  />
                </div>
                <div className="col-span-1 flex justify-end">
                  <Button size="sm" variant="ghost" onClick={() => removeZone(idx)} className="h-9 w-9 p-0 hover:bg-rose-950/40">
                    <Trash2 size={14} className="text-rose-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div>
            <Label className="text-muted-foreground text-xs uppercase tracking-wider">Notizen</Label>
            <Textarea
              data-testid="input-notes"
              value={draft.notes || ""}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              rows={3}
              className="bg-muted border-border mt-1 text-sm"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-muted-foreground hover:text-foreground">
              Abbrechen
            </Button>
            <Button
              data-testid="btn-save-setup"
              onClick={save}
              disabled={saving}
              className="bg-white text-black hover:bg-zinc-200 rounded-sm font-display font-bold uppercase tracking-wider"
            >
              {saving ? <><Loader2 size={14} className="mr-2 animate-spin" /> Speichern...</> :
                (isEdit ? "Änderungen speichern" : "Setup speichern")}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
